import React, { useState, useEffect } from "react";
import moment from "moment";
import {
  Box,
  Grid,
  TextField,
  IconButton,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import NotificationsIcon from "@mui/icons-material/Notifications";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import { DocumentAutocomplete } from "../../navigationList/navigationComponents/DocumentAutocomplete";
import { SectionList } from "../sharedComponents/SectionList";
import { AvatarHeader } from "../sharedComponents/Avatar";
import { Instructions } from "../sharedComponents/Instructions";
import { Loading } from "../../sharedComponents/Loading";
import { DataNotFound } from "../../sharedComponents/DataNotFound";
import { NotesPanel } from "../panel/Notes";
import { useFetchSections } from "../../../hooks/useGetSections";

import GenerateClipping from "../../panel/panelNotes/GenerateClipping";
import { ClippingDownload } from "../../panel/panelNotes/DownloadClipping";
import RecyclingBin from "../../user/panel/panelNotes/RecyclingBin";
import ThemeToggleButton from "../../../styles/editor/ThemeToggleEditor";
import { useFetchDocument } from "../../../hooks/useGetDocument";

export const ContentPanel = ({ token, userMe }) => {
  const [documentID, setDocumentID] = useState(null);
  const [nameDocument, setNameDocument] = useState("");
  const [selectedIdSection, setSelectedIdSection] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const [selectedDate, setSelectedDate] = useState(
    moment().format("YYYY-MM-DD")
  );
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("lg"));

  const {
    document: documents,
    loading: documentsLoading,
  } = useFetchDocument(token);

  const [showGenerateClipping, setShowGenerateClipping] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadQueue, setDownloadQueue] = useState([]);
  const [hiperlyncQueue, setHiperlyncQueue] = useState([]);
  const [showTrashModal, setShowTrashModal] = useState(false);
  const { sections, loading } = useFetchSections(token, documentID);

  useEffect(() => {
    if (sections.length > 0 && !selectedIdSection) {
      setSelectedIdSection(sections[0]?.ID_SECCION);
    }
  }, [sections, selectedIdSection]);

  const handleSelect = (item) => {
    setSelectedIdSection(item);
    setRefresh((prev) => !prev);
  };

  const addToDownloadQueue = (file) => {
    setDownloadQueue((prevQueue) => [...prevQueue, file]);
  };

  const addToHiperlyncQueue = (file) => {
    setHiperlyncQueue((prevQueue) => [...prevQueue, file]);
  };

  return (
    <Grid container style={{ height: "100%", overflow: "auto" }}>
      <Grid
        item
        xs={12}
        style={{
          height: "auto",
          display: "flex",
          alignItems: "center",
          padding: "5px 20px",
          margin: "5px 0",
          backgroundColor:
            theme.palette.mode === "dark" ? "#333336" : "#f5f5f5",
        }}
      >
        <Grid
          container
          spacing={2}
          alignItems="center"
          sx={{ overflowX: { xs: "auto", sm: "visible" } }}
        >
          <Grid
            item
            xs={12}
            md={6}
            container
            justifyContent={{ xs: "center", sm: "center", md: "flex-start" }}
          >
            <img
              style={{
                width: "100%",
                maxWidth: "200px",
                height: "auto",
                transition: "all 0.3s ease",
              }}
              src={"/mdm_logo.svg"}
              alt="Logo MDM"
            />
          </Grid>

          <Grid
            item
            xs={12}
            md={6}
            container
            spacing={1}
            justifyContent={{ xs: "center", sm: "flex-end" }}
            sx={{
              flexWrap: { xs: "wrap", sm: "nowrap" },
              overflowX: { xs: "auto", sm: "visible" },
            }}
          >
            <Grid
              item
              xs={12}
              md="auto"
              sx={{ width: { xs: "100%", sm: 250 }, maxWidth: { xs: "100%" } }}
            >
              <DocumentAutocomplete
                documentID={documentID}
                setDocumentID={setDocumentID}
                setNameDocument={setNameDocument}
                documents={documents}
                loading={documentsLoading}
              />
            </Grid>

            <Grid item xs={12} sm="auto">
              <TextField
                label="Fecha"
                variant="outlined"
                type="date"
                size="small"
                fullWidth
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                sx={{ minWidth: 150 }}
                inputProps={{
                  max: new Date().toISOString().split("T")[0],
                }}
                disabled={!documentID}
              />
            </Grid>

            <Grid
              item
              container
              xs={12}
              sm="auto"
              justifyContent="center"
              spacing={1}
            >
              <Grid item>
                <Tooltip title="Clipping" arrow>
                  <span>
                    <IconButton
                      onClick={() => setShowGenerateClipping(true)}
                      disabled={!documentID}
                    >
                      <AttachFileIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Grid>
              <Grid item>
                <Tooltip title="Descargas" arrow>
                  <span>
                    <IconButton
                      onClick={() => setShowDownloadModal(true)}
                      disabled={!documentID}
                      sx={{ position: "relative" }}
                    >
                      <NotificationsIcon />
                      {downloadQueue.length > 0 && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 3,
                            right: 3,
                            backgroundColor: "red",
                            color: "white",
                            borderRadius: "50%",
                            width: 20,
                            height: 20,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.75rem",
                            fontWeight: "bold",
                          }}
                        >
                          {downloadQueue.length}
                        </Box>
                      )}
                      {hiperlyncQueue.length > 0 && (
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: 2,
                            right: 2,
                            backgroundColor: "blue",
                            color: "white",
                            borderRadius: "50%",
                            width: 10,
                            height: 10,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                          }}
                        />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </Grid>
              <Grid item>
                <Tooltip title="Papelera" arrow>
                  <span>
                    <IconButton disabled={!documentID}>
                      <RestoreFromTrashIcon
                        onClick={() => {
                          setShowTrashModal(true);
                        }}
                      />
                    </IconButton>
                  </span>
                </Tooltip>
              </Grid>
              <Grid item>
                <ThemeToggleButton menuOpen={false}/>
              </Grid>
              <Grid item>
                <AvatarHeader userMe={userMe} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Grid container style={{ height: "88%" }}>
        {documentID ? (
          <>
            {loading ? (
              <Loading />
            ) : sections.length === 0 ? (
              <DataNotFound />
            ) : (
              <>
                <Grid
                  item
                  xs={12}
                  lg={2}
                  style={{
                    overflowY: "auto",
                    padding: "0 5px",
                    ...(isXs ? { height: "auto" } : { height: "100%" }),
                  }}
                >
                  <SectionList
                    sections={sections}
                    selectedIdSection={selectedIdSection}
                    handleSelect={handleSelect}
                    token={token}
                    idDocument={documentID}
                    selectedDate={selectedDate}
                    addToDownloadQueue={addToDownloadQueue}
                    addToHiperlyncQueue={addToHiperlyncQueue}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  lg={10}
                  style={{ height: "100%", overflowY: "auto", padding: "5px" }}
                >
                  <NotesPanel
                    token={token}
                    idDocument={documentID}
                    selectedDate={selectedDate}
                    selectedIdSection={selectedIdSection}
                    refresh={refresh}
                    setRefresh={setRefresh}
                  />
                </Grid>
              </>
            )}
          </>
        ) : (
          <Instructions />
        )}
      </Grid>

      <GenerateClipping
        open={showGenerateClipping}
        onClose={() => setShowGenerateClipping(false)}
        token={token}
        id_documento={documentID}
        nameDocument={nameDocument}
        fecha={selectedDate}
        addToDownloadQueue={addToDownloadQueue}
        addToHiperlyncQueue={addToHiperlyncQueue}
      />
      <ClippingDownload
        open={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        downloadQueue={downloadQueue}
        setDownloadQueue={setDownloadQueue}
        hiperlync={true}
        token={token}
        selectedDate={selectedDate}
        setHiperlyncQueue={setHiperlyncQueue}
        hiperlyncQueue={hiperlyncQueue}
      />
      <RecyclingBin
        open={showTrashModal}
        onClose={() => setShowTrashModal(false)}
        token={token}
        id_documento={documentID}
        fecha={selectedDate}
        onRestore={() => setRefresh((prev) => !prev)}
      />
    </Grid>
  );
};
